<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Chart, BarController, BarElement, LineElement, Title, Tooltip, TimeSeriesScale, LinearScale } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-adapter-date-fns'
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)
Chart.register(BarController, BarElement, LineElement, Title, Tooltip, TimeSeriesScale, LinearScale, annotationPlugin, ChartDataLabels)
Chart.defaults.color = '333'
Chart.defaults.font.size = 13

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  type: {
    type: String,
    default: '10m'
  }
})
const chart = ref(null)
const isError = ref(false)
const noData = ref(true)

const levelList = computed(() => {
  const list = [
    ...(props.data.warn_water_level ? [{ rank: 2, value: props.data.warn_water_level }] : []),
    ...(props.data.alarm_water_level ? [{ rank: 3, value: props.data.alarm_water_level }] : []),
    ...(props.data.danger_water_level ? [{ rank: 4, value: props.data.danger_water_level }] : []),
    ...(props.data.high_water_level ? [{ rank: 5, value: props.data.high_water_level }] : [])
  ]
  return list
})
const waterLevelData = computed(() => props.data[`observation_${props.type}`])
const currentWaterRank = computed(() => setRank(props.data.current_water_level, levelList.value))

watch(() => props.type, () => {
  drawChart()
})

function createGradient(ctx, chartArea) {
  const gradient = ref({})
  gradient.value.nrml = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.value.suii = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.value.warn = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.value.alarm = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.value.danger = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.value.high = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  // 通常
  gradient.value.nrml.addColorStop(0, 'rgba(20, 141, 255, 1)')
  gradient.value.nrml.addColorStop(0.8, 'rgba(20, 141, 255, 0.8)')
  gradient.value.nrml.addColorStop(1, 'rgba(20, 141, 255, 0.4)')
  // 水防団
  gradient.value.suii.addColorStop(0, 'rgba(111, 178, 138, 1)')
  gradient.value.suii.addColorStop(0.8, 'rgba(111, 178, 138, 0.8)')
  gradient.value.suii.addColorStop(1, 'rgba(111, 178, 138, 0.4)')
  // 氾濫注意
  gradient.value.warn.addColorStop(0, 'rgba(231, 220, 0, 1)')
  gradient.value.warn.addColorStop(0.8, 'rgba(231, 220, 0, 0.8)')
  gradient.value.warn.addColorStop(1, 'rgba(231, 220, 0, 0.4)')
  // 避難判断
  gradient.value.alarm.addColorStop(0, 'rgba(255, 40, 0, 1)')
  gradient.value.alarm.addColorStop(0.8, 'rgba(255, 40, 0, 0.8)')
  gradient.value.alarm.addColorStop(1, 'rgba(255, 40, 0, 0.4)')
  // 氾濫危険
  gradient.value.danger.addColorStop(0, 'rgba(170, 0, 170, 1)')
  gradient.value.danger.addColorStop(0.8, 'rgba(170, 0, 170, 0.8)')
  gradient.value.danger.addColorStop(1, 'rgba(170, 0, 170, 0.4)')
  // 計画高
  gradient.value.high.addColorStop(0, 'rgba(12, 0, 12, 0.8)')
  gradient.value.high.addColorStop(0.8, 'rgba(12, 0, 12, 0.6)')
  gradient.value.high.addColorStop(1, 'rgba(12, 0, 12, 0.4)')
  return gradient.value
}

function resetChart() {
  if (chart.value) chart.value.destroy()
}

const gradientColor = {
  5: 'high',
  4: 'danger',
  3: 'alarm',
  2: 'warn',
  1: 'suii',
  0: 'nrml'
}

let yMin
function setChartData(minMax) {
  const waterLevel2color = (val) => {
    let color = 'nrml'
    if (levelList.value.length === 0) {
      return color
    }
    levelList.value.forEach((item) => {
      if (item.value <= val) {
        color = gradientColor[item.rank]
      }
    })
    return color
  }

  const data = waterLevelData.value
  data.value = []
  if (!data) return

  const datasets = { labels: [], waterLvs: [], backgroundColors: [] }
  minMax.yMin = null
  minMax.yMax = null

  data.forEach((el, _) => {
    let val
    if (el.water_level != null) {
      if (minMax.yMin == null || minMax.yMin > el.water_level) {
        minMax.yMin = el.water_level
      }
      if (minMax.yMax == null || minMax.yMax < el.water_level) {
        minMax.yMax = el.water_level
      }
      val = el.water_level
    } else {
      val = null
    }
    datasets.labels.push(el.tm * 1000)
    datasets.waterLvs.push(val)
  })

  let thresholdsMin

  yMin = thresholdsMin == null
    ? Math.floor(Math.min(minMax.yMin))
    : minMax.yMin == null
      ? thresholdsMin <= 10
        ? Math.floor(thresholdsMin)
        : Math.floor(thresholdsMin - 10)
      : Math.floor(Math.min(minMax.yMin, thresholdsMin))
  if (yMin >= 0 && yMin < 10) {
    yMin = 0
  } else {
    yMin = yMin - 1
  }

  if (datasets.waterLvs.every((val) => val == null)) {
    noData.value = true
  } else {
    noData.value = false
  }

  const barValue = []
  for (const i of datasets.waterLvs) {
    if (yMin < 0 && i == null) {
      barValue.push(yMin)
    } else {
      barValue.push(i)
    }
  }
  datasets.barValue = barValue

  const chartData = {
    labels: datasets.labels,
    datasets: [{
      data: datasets.barValue,
      datalabels: {
        align: 'end',
        anchor: 'end',
        clamp: true,
        padding: {
          bottom: -10
        },
        formatter: function (value, _context) {
          if (yMin >= 0) {
            return value == null ? 'x' : ''
          }
          return value === yMin ? 'x' : ''
        }
      },
      backgroundColor: function (context) {
        const chart = context.chart
        const { ctx, chartArea } = chart

        if (!chartArea) {
          return null
        }
        const gradient = createGradient(ctx, chartArea)
        return gradient[waterLevel2color(context.raw)]
      },
      base: function (context) {
        const min = Math.min(...context.dataset.data)
        if (min > 0) {
          return 0
        }
        return min - 50
      },
      labelTimes: datasets.labels
    }]
  }
  return chartData
}

function setChartOptions() {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    animation: false,
    layout: {
      padding: {
        top: 10,
        left: 0,
        right: 10,
        bottom: 0
      }
    },
    scales: {
      x: {
        type: 'time',
        ticks: {
          minRotation: 0,
          maxRotation: 0,
          padding: 10
        },
        time: {
          unit: 'hour',
          displayFormats: {
            hour: 'H:mm'
          }
        },
        grid: {
          display: false,
          offset: false,
          drawTicks: false
        },
        border: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        min: yMin,
        ticks: {
          padding: 10
        },
        grid: {
          drawTicks: false
        },
        border: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'x',
      intersect: false
    },
    plugins: {
      datalabels: {
        color: '#333',
        font: {
          size: 12
        }
      },
      legend: {
        display: false
      },
      title: {
        display: true,
        text: '　m',
        position: 'top',
        align: 'start',
        padding: {
          bottom: 10
        },
        font: {
          weight: 'normal',
          size: 12
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        bodyColor: '#000',
        borderColor: '#ccc',
        borderWidth: 1,
        displayColors: false,
        padding: 8,
        titleColor: '#333',
        titleFont: {
          size: 13
        },
        titleAlign: 'center',
        titleMarginBottom: 10,
        callbacks: {
          title: function (context) {
            return formatDate(context[0].dataset.labelTimes[context[0].dataIndex])
          },
          label: function (context) {
            if (context.raw == null || (yMin < 0 && context.raw === yMin)) {
              return '水位:  -'
            } else {
              return `水位:  ${context.raw}m`
            }
          }
        }
      }
    }
  }

  // 基準線
  let annoMax = null
  const annotations = {}
  if (props.data.warn_water_level != null) {
    annotations.warn = {
      type: 'line',
      borderColor: '#FAF500',
      borderWidth: 3,
      yMin: props.data.warn_water_level,
      yMax: props.data.warn_water_level
    }
    if (annoMax == null || annoMax < props.data.warn_water_level) annoMax = props.data.warn_water_level
  }
  if (props.data.alarm_water_level != null) {
    annotations.alarm = {
      type: 'line',
      borderColor: '#E72800',
      borderWidth: 3,
      yMin: props.data.alarm_water_level,
      yMax: props.data.alarm_water_level
    }
    if (annoMax == null || annoMax < props.data.alarm_water_level) annoMax = props.data.alarm_water_level
  }
  if (props.data.danger_water_level != null) {
    annotations.danger = {
      type: 'line',
      borderColor: '#B500BF',
      borderWidth: 3,
      yMin: props.data.danger_water_level,
      yMax: props.data.danger_water_level
    }
    if (annoMax == null || annoMax < props.data.danger_water_level) annoMax = props.data.danger_water_level
  }
  if (props.data.high_water_level != null) {
    annotations.high = {
      type: 'line',
      borderColor: '#0C000C',
      borderWidth: 3,
      yMin: props.data.high_water_level,
      yMax: props.data.high_water_level
    }
    if (annoMax == null || annoMax < props.data.high_water_level) annoMax = props.data.high_water_level
  }
  chartOptions.plugins.annotation = {}
  chartOptions.plugins.annotation.annotations = annotations

  return chartOptions
}

function drawChart() {
  isError.value = false
  try {
    resetChart()
    const ctx = document.getElementById('riverChart')
    const minMax = {}
    const chartData = setChartData(minMax)
    const chartOptions = setChartOptions()
    if (!ctx || !chartOptions) return
    chart.value = new Chart(ctx, {
      plugins: [ChartDataLabels],
      type: 'bar',
      data: chartData,
      options: chartOptions
    })
  } catch (e) {
    isError.value = true
    console.error(e)
  }
}

function formatDate(date) {
  return dayjs(date).tz('Asia/Tokyo').format('M月D日(ddd) H:mm')
}

onMounted(() => {
  drawChart()
})
</script>

<template>
  <div class="head">
    <SectionTitle title="水位情報" />
  </div>
  <div class="body">
    <div class="waterLevel__card">
      <div class="waterLevel__card-info">
        <div class="waterLevel__card-date">
          <p class="waterLevel__card-time">
            {{ dayjs.unix(props.data.update_time).tz('Asia/Tokyo').format('M月D日 H:mm') }}時点
          </p>
        </div>
        <p class="waterLevel__card-value">
          {{ props.data.current_water_level || ' - ' }}<span>m</span>
        </p>
      </div>
      <p
        v-if="currentWaterRank!= 0"
        class="text"
        :data-rank="currentWaterRank"
      >
        {{ setRankText[currentWaterRank] }}
      </p>
    </div>
    <div class="graph">
      <figure>
        <canvas
          id="riverChart"
        />
      </figure>
      <div
        v-if="levelList.length > 0"
        class="data"
      >
        <div
          v-for="(item, index) in levelList"
          :key="index"
          class="item"
        >
          <p
            class="text"
            :data-rank="item.rank"
          >
            {{ setRankText[item.rank] }}
          </p>
          <p class="value">
            {{ item.value }}m
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.head {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-start;
}
.body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  .waterLevel__card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-radius: 4px;
    background-color: #1f59ba1a;
    .waterLevel__card-info {
      display: flex;
      align-items: center;
      gap: 10px;
      .waterLevel__card-date {
        display: flex;
        flex-direction: column;
        .waterLevel__card-time {
          color: #606369;
          text-align: center;
          font-size: 14px;
          font-weight: 300;
          line-height: 1em;
        }
      }
      .waterLevel__card-value {
        color: #303030;
        font-size: 32px;
        font-weight: 600;
        line-height: 1em;
        span {
          font-size: 25px;
        }
      }
    }
    .text {
      padding: 2px 5px;
      border-radius: 2px;
      color: #fff;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      line-height: 15px;
      &[data-rank='1'] {
        background-color: #6fb28a;
      }
      &[data-rank='2'] {
        color: #303030;
        background-color: #e7dc00;
      }
      &[data-rank='3'] {
        background-color: #ff2800;
      }
      &[data-rank='4'] {
        background-color: #b500bf;
      }
      &[data-rank='5'] {
        background-color: #0c000ccc;
      }
    }
  }
  .graph {
    display: flex;
    flex-direction: column;
    padding: 20px 10px;
    gap: 20px;
    background-color: #F6F6F6;
    figure {
      width: 100%;
      aspect-ratio: 2/1;
    }
  }
  .data {
    display: flex;
    gap: 2px;
    .item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
    .text {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding-block: 10px;
      color: #FFF;
      font-size: 14px;
      font-weight: 600;
      line-height: 1em;
      letter-spacing: .25px;
      text-align: center;
      &[data-rank='1'] {
        background-color: #6fb28a;
      }
      &[data-rank='2'] {
        color: #303030;
        background-color: #e7dc00;
      }
      &[data-rank='3'] {
        background-color: #ff2800;
      }
      &[data-rank='4'] {
        background-color: #b500bf;
      }
      &[data-rank='5'] {
        background-color: #0c000ccc;
      }
    }
    .value {
      color: #000;
      font-size: 14px;
      font-weight: 600;
      line-height: 1em;
      letter-spacing: .25px;
    }
  }
}
@media (min-width: 830px) {
  .body {
    .waterLevel__card {
      .text {
        font-size: 16px;
      }
    }
  }
  .graph {
    padding: 20px;
  }
  .data {
    .text {
      font-size: 16px;
    }
    .value {
      font-size: 16px;
    }
  }
}
</style>
